import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * Projekt-Kalkulation (ADR 0055): Zeilen in drei Bereichen (Material/Arbeit/
 * Sonderkosten) mit PLAN/IST je Zeile, GK-Stufung (HK → SK), Verkaufspreis und
 * Intercompany-Sichten. Abweichung = IST − PLAN (Konvention der abgelösten App).
 */

export const calcSectionSchema = z.enum(['MATERIAL', 'LABOR', 'SPECIAL']);
export type CalcSection = z.infer<typeof calcSectionSchema>;

export const projectGroupSchema = z.enum(['KOMMUNAL', 'INDUSTRIE']);
export type ProjectGroup = z.infer<typeof projectGroupSchema>;

export const projectCalcStatusSchema = z.enum([
  'VORKALKULATION_IN_ARBEIT',
  'VORKALKULATION_FERTIG',
  'NACHKALKULATION',
  'ABGESCHLOSSEN',
  'ABGESCHLOSSEN_GEPRUEFT',
]);
export type ProjectCalcStatus = z.infer<typeof projectCalcStatusSchema>;

// ── Zeilen schreiben (Replace-Semantik wie PUT /plan) ───────────────────────────

/** Eine Kalkulationszeile beim Speichern. */
export const putCalcLineSchema = z.object({
  section: calcSectionSchema,
  label: z.string().trim().min(1).max(200),
  /** MATERIAL: zugeordnete Hauptwarengruppen (je HWG projektweit max. 1 Zeile). */
  mainGroupCodes: z.array(z.number().int().min(1000).max(9999)).max(20).default([]),
  /** LABOR: Position (Soft-Ref); Name wird serverseitig gesnapshottet. */
  positionId: z.string().uuid().nullable().optional(),
  /** LABOR: Plan-Stunden. */
  plannedQty: z.number().min(0).max(1_000_000).nullable().optional(),
  /** LABOR: Stundensatz (Money) — projektspezifischer Snapshot. */
  plannedRate: moneySchema.nullable().optional(),
  /** MATERIAL/SPECIAL: Plan-Betrag; LABOR: wird serverseitig aus Qty × Satz gerechnet. */
  plannedAmount: moneySchema.nullable().optional(),
  /** Ist-Override (ERSETZT den ERP-Wert; SPECIAL: der Ist-Wert selbst). */
  actualOverride: moneySchema.nullable().optional(),
  /** Manuelle Ist-Stunden (LABOR, rein informativ zum Override). */
  actualManualQty: z.number().min(0).max(1_000_000).nullable().optional(),
  /** Entity, in der die Kosten anfallen; null = Owner-Gesellschaft des Projekts. */
  legalEntityId: z.string().uuid().nullable().optional(),
});
export type PutCalcLineInput = z.infer<typeof putCalcLineSchema>;

/** Alle Zeilen eines Projekts ersetzen (Reihenfolge = sortIndex). */
export const putProjectCalcLinesSchema = z.object({
  lines: z.array(putCalcLineSchema).max(200),
});
export type PutProjectCalcLinesInput = z.infer<typeof putProjectCalcLinesSchema>;

// ── Read-Model ──────────────────────────────────────────────────────────────────

/** Plan/Ist/Abweichung als Money-Tripel (Abweichung = IST − PLAN). */
export const planActualSchema = z.object({
  plan: moneySchema,
  actual: moneySchema,
  variance: moneySchema,
});
export type PlanActualDto = z.infer<typeof planActualSchema>;

/** Eine Kalkulationszeile in der Antwort (inkl. generierter Systemzeilen). */
export const calcLineResponseSchema = z.object({
  /** null bei generierten Systemzeilen („Nicht zugeordnet …"). */
  id: z.string().uuid().nullable(),
  section: calcSectionSchema,
  label: z.string(),
  sortIndex: z.number().int(),
  mainGroupCodes: z.array(z.number().int()),
  positionId: z.string().uuid().nullable(),
  positionName: z.string().nullable(),
  plannedQty: z.number().nullable(),
  plannedRate: moneySchema.nullable(),
  /** Ist-Stunden aus den Zeitbuchungen (LABOR); manuelle Ist-Stunden beim Override. */
  actualQty: z.number().nullable(),
  amounts: planActualSchema,
  /** ERP-Ist vor Override (null = keine ERP-Quelle) — für den „manuell"-Marker. */
  actualErp: moneySchema.nullable(),
  /** true = Ist wurde manuell überschrieben (Override aktiv). */
  isOverridden: z.boolean(),
  /** true = generierte Systemzeile (nicht editierbar, z. B. nicht zugeordnete HWG). */
  isSystem: z.boolean(),
  legalEntityId: z.string().uuid().nullable(),
  legalEntityCode: z.string().nullable(),
});
export type CalcLineResponse = z.infer<typeof calcLineResponseSchema>;

/** Kennzahl in % (1 Nachkommastelle) für Plan und Ist; null ohne Erlös. */
export const calcPctPairSchema = z.object({
  plan: z.number().nullable(),
  actual: z.number().nullable(),
});

/** Summenblock einer Sicht (Gesamt oder Entity): HK → SK → VK → Ergebnisse. */
export const calcSummarySchema = z.object({
  material: planActualSchema,
  materialOverhead: planActualSchema,
  materialTotal: planActualSchema,
  labor: planActualSchema,
  special: planActualSchema,
  hk: planActualSchema,
  salesOverhead: planActualSchema,
  sk: planActualSchema,
  /** Erlös (Gesamt/Owner: VK; Liefer-Entity: IC-Verrechnungspreis). */
  revenuePlan: moneySchema.nullable(),
  revenueActual: moneySchema.nullable(),
  resultSkPlan: moneySchema.nullable(),
  resultSkActual: moneySchema.nullable(),
  resultHkPlan: moneySchema.nullable(),
  resultHkActual: moneySchema.nullable(),
  wesPct: calcPctPairSchema,
  resultSkPct: calcPctPairSchema,
  resultHkPct: calcPctPairSchema,
});
export type CalcSummaryDto = z.infer<typeof calcSummarySchema>;

/** Art eines Zukauf-Blocks im Verrechnungspreis (Marge aliquot enthalten). */
export const icBreakdownKindSchema = z.enum(['MATERIAL', 'LABOR', 'SPECIAL']);
export type IcBreakdownKind = z.infer<typeof icBreakdownKindSchema>;

/** Ein Bestandteil des Verrechnungspreises (Plan-fixiert, Plan = Ist; Marge anteilig eingerechnet). */
export const icBreakdownRowSchema = z.object({
  kind: icBreakdownKindSchema,
  /** Anzeige-Label, z. B. „Zukauf Material Probig AT". */
  label: z.string(),
  amount: moneySchema,
});
export type IcBreakdownRow = z.infer<typeof icBreakdownRowSchema>;

/** GuV-Sicht einer Gesellschaft (ADR 0055, Excel-Modell 2026-07-20). */
export const calcEntityViewSchema = z.object({
  legalEntityId: z.string().uuid().nullable(),
  legalEntityCode: z.string().nullable(),
  legalEntityName: z.string().nullable(),
  /** true = hält den Kundenauftrag (Erlös = VK, Zukäufe als Kostenblock). */
  isOwner: z.boolean(),
  /**
   * Berechneter Verrechnungspreis (Plan-fixiert): Liefer-Entity = ihr Erlös;
   * Owner = Summe aller Zukauf-Blöcke, die in den Sonderkosten stecken.
   */
  icTransferPrice: moneySchema.nullable(),
  /** Owner: die Zukauf-Blöcke (Material inkl. MGK / Abwicklung / Sonderpositionen / Marge). */
  icBreakdown: z.array(icBreakdownRowSchema),
  summary: calcSummarySchema,
});
export type CalcEntityView = z.infer<typeof calcEntityViewSchema>;

/** Vollständige Kalkulation eines Projekts. */
export const projectCalculationResponseSchema = z.object({
  projectId: z.string().uuid(),
  currency: z.enum(['EUR', 'USD', 'CHF']),
  calcStatus: projectCalcStatusSchema,
  materialOverheadPct: z.number(),
  salesOverheadPct: z.number(),
  /** Eigener Vertriebs-GK-Satz der Owner-Gesellschaft (Excel: 7,5 %). */
  ownerSalesOverheadPct: z.number(),
  /** Plan-VK (Projekt-Feld) und Ist-VK (= Auftragswert). */
  planSalesPrice: moneySchema.nullable(),
  actualSalesPrice: moneySchema.nullable(),
  lines: z.array(calcLineResponseSchema),
  /** Gesamt-Sicht (Kundensicht, ohne Intercompany-Verrechnung). */
  total: calcSummarySchema,
  /** Entity-Sichten (leer, wenn alle Zeilen bei der Owner-Gesellschaft liegen). */
  entityViews: z.array(calcEntityViewSchema),
  /**
   * Intercompany: EFFEKTIVER Verrechnungspreis (manuell verhandelt, sonst =
   * Vorschlag; null ohne Liefer-Entity) + Ziel-Gewinnanteil + Vorschlag.
   */
  icTransferPrice: moneySchema.nullable(),
  /** true = am Projekt manuell gepflegt (verhandelt); false = Vorschlag gilt. */
  isIcTransferPriceManual: z.boolean(),
  icProfitSplitPct: z.number(),
  /** Vorschlag: Liefer-Plan-Kosten (inkl. MGK) + split% vom Plan-Ergebnis HK. */
  icSuggestion: moneySchema.nullable(),
});
export type ProjectCalculationResponse = z.infer<typeof projectCalculationResponseSchema>;

/** Anzeige-Labels des Kalkulations-Status. */
export const PROJECT_CALC_STATUS_LABELS: Record<ProjectCalcStatus, string> = {
  VORKALKULATION_IN_ARBEIT: 'Vorkalkulation in Arbeit',
  VORKALKULATION_FERTIG: 'Vorkalkulation fertig',
  NACHKALKULATION: 'Nachkalkulation',
  ABGESCHLOSSEN: 'Abgeschlossen',
  ABGESCHLOSSEN_GEPRUEFT: 'Abgeschlossen & geprüft',
};

/** Anzeige-Labels der Projektgruppe (Deutsch). */
export const PROJECT_GROUP_LABELS: Record<ProjectGroup, string> = {
  KOMMUNAL: 'Kommunal',
  INDUSTRIE: 'Industrie',
};

/** Anzeige-Labels der Projektgruppe (Englisch) — für zweisprachige Exporte (Referenzen-PDF). */
export const PROJECT_GROUP_LABELS_EN: Record<ProjectGroup, string> = {
  KOMMUNAL: 'Municipal',
  INDUSTRIE: 'Industrial',
};

/** Projektgruppen-Label je Sprache (Muster: `formatUnit` in article.schema.ts). */
export function formatProjectGroup(group: ProjectGroup | null | undefined, lang: 'de' | 'en' = 'de'): string {
  if (!group) return '';
  return (lang === 'en' ? PROJECT_GROUP_LABELS_EN : PROJECT_GROUP_LABELS)[group] ?? group;
}
